/**
 * UI event object delivered to Vue handlers (`@click`, `@pointerdown`, …) by
 * {@link UIInputSystem}. DOM-like but minimal: target/currentTarget + stopPropagation.
 */
import type { UINode } from '../tree/UINode';

export type UIPointerEventType =
  | 'pointerenter'
  | 'pointerleave'
  | 'pointermove'
  | 'pointerdown'
  | 'pointerup'
  | 'click'
  | 'wheel';

export type UIFocusEventType = 'focus' | 'blur' | 'cancel';

export type UIEventType = UIPointerEventType | UIFocusEventType;

export interface UIEventInit {
  /** Pointer position in LOGICAL canvas units (layout space). */
  x?: number;
  y?: number;
  /** 0 = left/primary, 1 = middle, 2 = right (pointerdown/up/click). */
  button?: number;
  /** Wheel deltas (raw device units). */
  deltaX?: number;
  deltaY?: number;
}

export class UIEvent {
  readonly type: UIEventType;
  /** Deepest hit node (never changes during bubbling). */
  readonly target: UINode;
  /** Node whose handler is currently being invoked. */
  currentTarget: UINode;
  readonly x: number;
  readonly y: number;
  readonly button: number;
  readonly deltaX: number;
  readonly deltaY: number;

  private stopped = false;

  constructor(type: UIEventType, target: UINode, init: UIEventInit = {}) {
    this.type = type;
    this.target = target;
    this.currentTarget = target;
    this.x = init.x ?? 0;
    this.y = init.y ?? 0;
    this.button = init.button ?? 0;
    this.deltaX = init.deltaX ?? 0;
    this.deltaY = init.deltaY ?? 0;
  }

  stopPropagation(): void {
    this.stopped = true;
  }

  get propagationStopped(): boolean {
    return this.stopped;
  }
}
